import React, { SyntheticEvent, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Tab, Grid, Header, Card, Image, TabProps, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useStore } from "../../app/stores/store";
import { useTranslation } from 'react-i18next';
import FriendRequestButton from '../friends/FriendRequestButton';
import { format } from 'date-fns';
import { sr } from 'date-fns/locale';
import i18n from '../../i18n';
import { FriendDto } from '../../app/models/FriendDto';

export default observer(function ProfileFriends() {
    const { friendListStore, profileStore } = useStore();
    const { loadFriends, friends, loadingFriends } = friendListStore;
    const { profile, isCurrentUser } = profileStore;
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Define panes based on whether it's current user
    const panes = isCurrentUser 
        ? [
            { menuItem: t('profileFriends.friends'), pane: { key: 'friends' } },
            { menuItem: t('profileFriends.requestsSent'), pane: { key: 'sent' } },
            { menuItem: t('profileFriends.requestsReceived'), pane: { key: 'received' } },
          ]
        : [
            { menuItem: t('profileFriends.friends'), pane: { key: 'friends' } },
          ];

    useEffect(() => {
        if (profile) {
            const predicate = panes[activeIndex].pane.key;
            loadFriends(profile.userID, predicate);
        }
    }, [loadFriends, profile, activeIndex]);

    const handleTabChange = (e: SyntheticEvent, data: TabProps) => {
        setActiveIndex(data.activeIndex as number);
    };

    const formatDate = (date: string | undefined) => {
        if (!date) return '';
        try {
            // Get the locale mapping for date-fns
            const getDateFnsLocale = () => {
                try {
                    const lang = i18n.language.split('-')[0]; // Get base language code (e.g., 'sr' from 'sr-Latn')
                    return require(`date-fns/locale/${lang}/index.js`).default;
                } catch {
                    return undefined; // Fall back to user's browser locale
                }
            };

            const locale = getDateFnsLocale();
            // Use localized medium date format (adapts to user's locale)
            return format(new Date(date), 'P', { locale });
        } catch (error) {
            console.error('Date formatting error:', error);
            // Fallback to browser's default locale formatting
            return new Date(date).toLocaleDateString();
        }
    };

    const renderFriendCard = (friend: FriendDto) => (
        <Card key={friend.userId} fluid>
            <Card.Content>
                {isMobile ? (
                    // Mobile Layout: Stack everything vertically
                    <Grid>
                        <Grid.Row>
                            <Grid.Column width={4} textAlign='center'>
                                <Link to={`/profiles/${friend.userId}`}>
                                    <Image
                                        src={friend.imageUrl || '/assets/user.png'}
                                        circular
                                        style={{ 
                                            width: '60px',
                                            height: '60px',
                                            objectFit: 'cover',
                                            margin: '0 auto',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Link>
                            </Grid.Column>
                            <Grid.Column width={12}>
                                <Link 
                                    to={`/profiles/${friend.userId}`} 
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <Card.Header 
                                        style={{ 
                                            fontSize: '1.1em',
                                            marginBottom: '0.5em',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {friend.fullName}
                                    </Card.Header>
                                </Link>
                                <Card.Meta style={{ fontSize: '0.85em' }}>
                                    {friend.friendsSince && (
                                        <span>
                                            <Icon name='calendar' />
                                            {t('profileFriends.friendsSince')}: {formatDate(friend.friendsSince)}
                                        </span>
                                    )}
                                    {friend.requestedAt && (
                                        <span>
                                            <Icon name='calendar' />
                                            {t('profileFriends.requestedAt')}: {formatDate(friend.requestedAt)}
                                        </span>
                                    )}
                                </Card.Meta>
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row style={{ paddingTop: '0.5em' }}>
                            <Grid.Column width={16}>
                                <FriendRequestButton 
                                    userId={friend.userId}
                                    onStatusChange={() => {
                                        // Reload the current tab after action
                                        if (profile) {
                                            const predicate = panes[activeIndex].pane.key;
                                            loadFriends(profile.userID, predicate);
                                        }
                                    }}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                ) : (
                    // Desktop Layout: Original horizontal layout
                    <Grid verticalAlign='middle'>
                        <Grid.Row>
                            <Grid.Column width={3} textAlign='center'>
                                <Link to={`/profiles/${friend.userId}`}>
                                    <Image
                                        src={friend.imageUrl || '/assets/user.png'}
                                        circular
                                        style={{ 
                                            width: '80px',
                                            height: '80px',
                                            objectFit: 'cover',
                                            margin: '0 auto',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Link>
                            </Grid.Column>
                            <Grid.Column width={10}>
                                <Link 
                                    to={`/profiles/${friend.userId}`} 
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <Card.Header 
                                        style={{ 
                                            fontSize: '1.3em',
                                            marginBottom: '0.5em',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {friend.fullName}
                                    </Card.Header>
                                </Link>
                                <Card.Meta style={{ fontSize: '0.9em' }}>
                                    {friend.friendsSince && (
                                        <span>
                                            <Icon name='calendar' />
                                            {t('profileFriends.friendsSince')}: {formatDate(friend.friendsSince)}
                                        </span>
                                    )}
                                    {friend.requestedAt && (
                                        <span>
                                            <Icon name='calendar' />
                                            {t('profileFriends.requestedAt')}: {formatDate(friend.requestedAt)}
                                        </span>
                                    )}
                                </Card.Meta>
                            </Grid.Column>
                            <Grid.Column width={3} textAlign='center'>
                                <FriendRequestButton 
                                    userId={friend.userId}
                                    onStatusChange={() => {
                                        // Reload the current tab after action
                                        if (profile) {
                                            const predicate = panes[activeIndex].pane.key;
                                            loadFriends(profile.userID, predicate);
                                        }
                                    }}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                )}
            </Card.Content>
        </Card>
    );

    return (
        <Tab.Pane loading={loadingFriends}>
            <Grid>
                <Grid.Column width={16}>
                    <Header 
                        floated='left' 
                        icon='users' 
                        content={t('profileFriends.title')} 
                    />
                </Grid.Column>
                <Grid.Column width={16}>
                    <Tab
                        panes={panes}
                        activeIndex={activeIndex}
                        menu={{ 
                            secondary: true, 
                            pointing: true,
                            stackable: true
                        }}
                        onTabChange={handleTabChange}
                    />
                    <br />
                    {friends.length === 0 ? (
                        <Card fluid>
                            <Card.Content textAlign='center' style={{ padding: '3em' }}>
                                <Icon 
                                    name='users' 
                                    size='huge' 
                                    color='grey' 
                                    style={{ marginBottom: '1em' }}
                                />
                                <Header as='h3' color='grey'>
                                    {activeIndex === 0 && t('profileFriends.noFriends')}
                                    {activeIndex === 1 && t('profileFriends.noSentRequests')}
                                    {activeIndex === 2 && t('profileFriends.noReceivedRequests')}
                                </Header>
                            </Card.Content>
                        </Card>
                    ) : (
                        <Card.Group itemsPerRow={1}>
                            {friends.map(friend => renderFriendCard(friend))}
                        </Card.Group>
                    )}
                </Grid.Column>
            </Grid>
        </Tab.Pane>
    );
});